// Form fields of each provider, written by hand from the crm_service models
// (backend/crm_service/src/exporters/<provider>/models.py and
// src/stock/<provider>/models.py). Keep them in sync when a model changes.
//
// Field `name` is the form key: `credentials.<path>` / `settings.<path>`, or a
// top-level column of the row. Its label is `integrations.fields.<name>` for
// the common columns and `integrations.providers.<provider>.<path>` for the
// provider ones. kind: text (default) | secret | int | email | select | list | intList | optionalBool
// (yes / no / left out, so the provider's default).

export const PROVIDERS = {
  syonet: {
    credentials: [
      { name: 'credentials.user', required: true },
      { name: 'credentials.password', kind: 'secret', required: true },
    ],
    settings: [
      { name: 'settings.company_id', kind: 'int', required: true },
      { name: 'settings.event_group', required: true },
      { name: 'settings.event_type', required: true },
      { name: 'settings.source' },
      { name: 'settings.media' },
      { name: 'settings.user_id', kind: 'int' },
      { name: 'settings.days_to_update_open_event', kind: 'int' },
      { name: 'settings.update_main_email_phone', kind: 'optionalBool' },
    ],
  },
  dealerspace: {
    defaultApiUrl: 'https://www.followize.com.br/api/v2/Leads',
    credentials: [
      { name: 'credentials.client_key', required: true },
      { name: 'credentials.team_key' },
      // DealerspaceOAuth: optional as a whole, but client_id/client_secret go together.
      { name: 'credentials.oauth.client_id', group: 'oauth' },
      { name: 'credentials.oauth.client_secret', kind: 'secret', group: 'oauth' },
      { name: 'credentials.oauth.redirect_uri', group: 'oauth' },
    ],
    settings: [
      { name: 'settings.conversion_goal', required: true },
      { name: 'settings.flow', kind: 'select', options: ['web', 'store'], default: 'web', required: true },
      { name: 'settings.attendant_email', kind: 'email' },
    ],
  },
  adset: {
    defaultApiUrl: 'https://www.adset.com.br/integrador/api/estoqueveiculos',
    credentials: [
      { name: 'credentials.email', required: true },
      { name: 'credentials.password', kind: 'secret', required: true },
    ],
    settings: [{ name: 'settings.cnpjs', kind: 'list', required: true }],
  },
  autogestor: {
    defaultApiUrl: 'https://agintegracao.com/api/v1',
    credentials: [
      { name: 'credentials.client_id', kind: 'int', required: true },
      { name: 'credentials.login', required: true },
      { name: 'credentials.password', kind: 'secret', required: true },
    ],
    settings: [{ name: 'settings.enderecos', kind: 'intList' }],
  },
};

// OAuth fields the Dealerspace backend requires once any OAuth field is sent.
const OAUTH_REQUIRED = ['credentials.oauth.client_id', 'credentials.oauth.client_secret'];

// StockConfig.bot_id: CharField(max_length=32).
export const BOT_ID_MAX_LENGTH = 32;

export const HOURS = Array.from({ length: 24 }, (_, hour) => String(hour));

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INT_PATTERN = /^-?\d+$/;

const pathOf = (name) => name.split('.').slice(1);

const getIn = (object, path) => path.reduce((node, key) => node?.[key], object);

const setIn = (object, path, value) => {
  const [head, ...rest] = path;
  return { ...object, [head]: rest.length ? setIn(object[head] ?? {}, rest, value) : value };
};

const splitList = (text) => text.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean);

const isEmpty = (value) => value === '' || value === undefined || value === null;

// Stored value → form value (strings; optionalBool: '' | 'true' | 'false').
const toInput = (field, value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (isEmpty(value)) return field.default ?? '';
  return String(value);
};

// Form value → payload value; undefined = leave the key out, null = remove the stored credential.
const fromInput = (field, value) => {
  if (value === null) return null;
  const text = String(value ?? '').trim();
  if (!text) return undefined;
  if (field.kind === 'optionalBool') return text === 'true';
  if (field.kind === 'int') return Number(text);
  if (field.kind === 'list') return splitList(text);
  if (field.kind === 'intList') return splitList(text).map(Number);
  return text;
};

// "08:00:00" → "8"; null → "" (any time).
const hourOf = (time) => (time ? String(Number(time.split(':')[0])) : '');
const timeOf = (hour) => (hour === '' ? null : `${hour.padStart(2, '0')}:00:00`);

/** Initial form values: the stored row when editing (credentials always blank), defaults when creating. */
export function initialValues(type, provider, config) {
  const spec = PROVIDERS[provider];
  const values = {
    api_url: config?.api_url ?? spec.defaultApiUrl ?? '',
    enabled: config?.enabled ?? true,
  };
  if (type === 'crm') {
    values.export_on_lead_created = config?.export_on_lead_created ?? false;
    values.export_on_lead_expired = config?.export_on_lead_expired ?? true;
  } else {
    values.bot_id = config?.bot_id ?? '';
    values.sync_window_start = hourOf(config?.sync_window_start);
    values.sync_window_end = hourOf(config?.sync_window_end);
  }
  spec.credentials.forEach((field) => {
    values[field.name] = '';
  });
  spec.settings.forEach((field) => {
    values[field.name] = toInput(field, getIn(config?.settings, pathOf(field.name)));
  });
  return values;
}

/**
 * Client-side mirror of the backend rules. `creating`: credentials are only
 * required then, since a blank credential on edit keeps the stored one.
 * Returns { [fieldName]: { key, params } } for `integrations.validation.<key>`.
 */
export function validate(type, provider, values, { creating }) {
  const spec = PROVIDERS[provider];
  const errors = {};
  const check = (name, rule) => {
    if (!errors[name] && rule) errors[name] = rule;
  };

  check('api_url', isEmpty(values.api_url?.trim()) && { key: 'required' });

  [...spec.credentials, ...spec.settings].forEach((field) => {
    const value = values[field.name];
    const text = typeof value === 'string' ? value.trim() : value;
    const isCredential = field.name.startsWith('credentials.');
    if (field.required && (creating || !isCredential)) {
      check(field.name, isEmpty(text) && { key: 'required' });
    }
    if (isEmpty(text)) return;
    if (field.kind === 'int') check(field.name, !INT_PATTERN.test(text) && { key: 'integer' });
    if (field.kind === 'intList') check(field.name, splitList(text).some((item) => !INT_PATTERN.test(item)) && { key: 'integerList' });
    if (field.kind === 'email') check(field.name, !EMAIL_PATTERN.test(text) && { key: 'email' });
  });

  // A new OAuth block needs both keys; on edit the stored ones may complete it, so the backend decides.
  if (creating && spec.credentials.some((field) => field.group === 'oauth' && !isEmpty(values[field.name]?.trim()))) {
    OAUTH_REQUIRED.forEach((name) => check(name, isEmpty(values[name]?.trim()) && { key: 'oauthPair' }));
  }

  if (type === 'stock') {
    // Nullable in StockConfig, but the agent needs it to answer with this inventory.
    check('bot_id', isEmpty((values.bot_id ?? '').trim()) && { key: 'required' });
    check('bot_id', (values.bot_id ?? '').trim().length > BOT_ID_MAX_LENGTH && { key: 'maxLength', params: { max: BOT_ID_MAX_LENGTH } });
    const start = values.sync_window_start;
    const end = values.sync_window_end;
    if ((start === '') !== (end === '')) {
      check(start === '' ? 'sync_window_start' : 'sync_window_end', { key: 'windowPair' });
    } else if (start !== '' && start === end) {
      check('sync_window_end', { key: 'windowEqual' });
    }
  }
  return errors;
}

// Starts from the stored settings without the keys the form owns, so a key it doesn't know survives.
const buildSettings = (spec, values, stored = {}) => {
  const owned = new Set(spec.settings.map((field) => pathOf(field.name)[0]));
  const kept = Object.fromEntries(Object.entries(stored ?? {}).filter(([key]) => !owned.has(key)));
  return spec.settings.reduce((settings, field) => {
    const value = fromInput(field, values[field.name]);
    return value === undefined ? settings : setIn(settings, pathOf(field.name), value);
  }, kept);
};

// Only the credentials typed in (or cleared: null): on create, the whole object; on edit, a JSON merge patch.
const buildCredentials = (spec, values) =>
  spec.credentials.reduce((credentials, field) => {
    const value = fromInput(field, values[field.name]);
    return value === undefined ? credentials : setIn(credentials, pathOf(field.name), value);
  }, {});

const ROW_FIELDS = {
  crm: ['api_url', 'enabled', 'export_on_lead_created', 'export_on_lead_expired'],
  stock: ['api_url', 'enabled', 'bot_id'],
};

const rowValue = (name, value) => {
  if (typeof value !== 'string') return value;
  return value.trim();
};

/**
 * POST body when creating; when editing, a PATCH body with only what changed
 * (`initial` = initialValues of the stored row, `stored` = its settings). `{}` = nothing to save.
 * Customer-wide for now: a CRM row goes without business_uuid, the default.
 */
export function buildPayload(type, provider, values, { creating, initial, stored }) {
  const spec = PROVIDERS[provider];
  const payload = {};
  ROW_FIELDS[type].forEach((name) => {
    if (creating || rowValue(name, values[name]) !== rowValue(name, initial[name])) {
      payload[name] = rowValue(name, values[name]);
    }
  });
  if (type === 'stock') {
    const changed = ['sync_window_start', 'sync_window_end'].some((name) => values[name] !== initial[name]);
    if (creating || changed) {
      payload.sync_window_start = timeOf(values.sync_window_start);
      payload.sync_window_end = timeOf(values.sync_window_end);
    }
  }

  // settings is replaced as a whole, so it goes complete whenever any of it changed.
  const settings = buildSettings(spec, values, stored);
  if (creating || JSON.stringify(settings) !== JSON.stringify(buildSettings(spec, initial, stored))) {
    payload.settings = settings;
  }

  const credentials = buildCredentials(spec, values);
  if (creating || Object.keys(credentials).length > 0) payload.credentials = credentials;

  return creating ? { provider, ...payload } : payload;
}

/** Read-only text of a stored setting, for the detail page. */
export function displayValue(provider, field, value, t) {
  if (field.kind === 'optionalBool') {
    if (isEmpty(value)) return t('integrations.values.providerDefault');
    return t(value ? 'integrations.values.yes' : 'integrations.values.no');
  }
  if (isEmpty(value) || (Array.isArray(value) && value.length === 0)) return null;
  if (Array.isArray(value)) return value.join(', ');
  if (field.kind === 'select') return t(optionLabelKey(provider, field.name, value));
  return String(value);
}

export const settingValue = (config, field) => getIn(config?.settings, pathOf(field.name));

// i18n key of a field's label: common columns under `fields`, provider ones under `providers.<key>`.
export const fieldLabelKey = (provider, name) =>
  (name.includes('.') ? `integrations.providers.${provider}.${pathOf(name).join('.')}` : `integrations.fields.${name}`);

// Optional credentials outside a group can be removed on edit (team_key). The OAuth block can't:
// dropping it also drops the tokens, which only internal users write.
export const isClearable = (field, creating) =>
  !creating && field.name.startsWith('credentials.') && !field.required && !field.group;

export const optionLabelKey = (provider, name, option) => `${fieldLabelKey(provider, name)}Options.${option}`;
